(function () {
  function showPreviewImageFallback(image) {
    if (!image) return;
    const previewItem = image.closest('[data-template-preview-item]') || image.closest('[data-template-preview]');
    const fallback = previewItem ? previewItem.querySelector('[data-template-image-fallback]') : null;
    image.hidden = true;
    if (fallback) fallback.hidden = false;
  }

  function defineTemplateLibrary() {
    if (customElements.get('plaud-template-library')) return;

    class PlaudTemplateLibrary extends HTMLElement {
      constructor() {
        super();
        this.isInitialized = false;
        this.activeCategory = 'all';
        this.cards = [];
        this.categoryButtons = [];
        this.searchInput = null;
        this.clearButton = null;
        this.resultCount = null;
        this.noResults = null;
        this.previewImages = [];
        this.detailLinks = [];

        this.handleClick = this.handleClick.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleImageError = this.handleImageError.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
      }

      connectedCallback() {
        if (this.isInitialized) return;

        this.cards = Array.from(this.querySelectorAll('[data-template-card]'));
        if (!this.cards.length) return;

        this.isInitialized = true;
        this.categoryButtons = Array.from(this.querySelectorAll('[data-template-category-filter]'));
        this.searchInput = this.querySelector('[data-template-search-input]');
        this.clearButton = this.querySelector('[data-template-search-clear]');
        this.resultCount = this.querySelector('[data-template-result-count]');
        this.noResults = this.querySelector('[data-template-no-results]');
        this.previewImages = Array.from(this.querySelectorAll('[data-template-preview] img'));
        this.detailLinks = Array.from(this.querySelectorAll('[data-template-detail-link]'));

        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleInput);
        this.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('popstate', this.handlePopState);
        this.previewImages.forEach((image) => {
          image.addEventListener('error', this.handleImageError);
          if (image.complete && image.naturalWidth === 0) this.showImageFallback(image);
        });

        this.querySelectorAll('[data-library-controls]').forEach((control) => {
          control.hidden = false;
        });
        this.classList.add('is-enhanced');
        this.restoreFiltersFromUrl(false);
      }

      disconnectedCallback() {
        if (!this.isInitialized) return;

        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('input', this.handleInput);
        this.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('popstate', this.handlePopState);
        this.previewImages.forEach((image) => image.removeEventListener('error', this.handleImageError));
        this.cards = [];
        this.categoryButtons = [];
        this.searchInput = null;
        this.clearButton = null;
        this.resultCount = null;
        this.noResults = null;
        this.previewImages = [];
        this.detailLinks = [];
        this.isInitialized = false;
      }

      handleClick(event) {
        const categoryButton = event.target.closest('[data-template-category-filter]');
        const clearButton = event.target.closest('[data-template-search-clear]');
        const resetButton = event.target.closest('[data-template-reset]');

        if (categoryButton && this.contains(categoryButton)) {
          this.setCategory(categoryButton.dataset.templateCategoryFilter || 'all', true);
          return;
        }

        if (clearButton && this.contains(clearButton)) {
          this.clearSearch(true);
          return;
        }

        if (resetButton && this.contains(resetButton)) {
          this.resetFilters();
        }
      }

      handleInput(event) {
        if (event.target !== this.searchInput) return;
        this.applyFilters(true);
        this.syncUrlState();
      }

      handleKeyDown(event) {
        const currentButton = event.target.closest('[data-template-category-filter]');
        if (!currentButton || !this.contains(currentButton)) return;
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;

        const currentIndex = this.categoryButtons.indexOf(currentButton);
        if (currentIndex < 0 || this.categoryButtons.length < 2) return;

        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.categoryButtons.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % this.categoryButtons.length;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + this.categoryButtons.length) % this.categoryButtons.length;
        }

        event.preventDefault();
        this.categoryButtons[nextIndex].focus();
      }

      handleImageError(event) {
        this.showImageFallback(event.target);
      }

      handlePopState() {
        this.restoreFiltersFromUrl(true);
      }

      showImageFallback(image) {
        showPreviewImageFallback(image);
      }

      setCategory(category, syncUrl) {
        this.activeCategory = category;
        this.categoryButtons.forEach((button) => {
          const isActive = button.dataset.templateCategoryFilter === category;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        this.applyFilters(true);
        if (syncUrl) this.syncUrlState();
      }

      clearSearch(returnFocus) {
        if (!this.searchInput) return;
        this.searchInput.value = '';
        this.applyFilters(true);
        this.syncUrlState();
        if (returnFocus) this.searchInput.focus();
      }

      resetFilters() {
        if (this.searchInput) this.searchInput.value = '';
        this.setCategory('all', true);
        if (this.searchInput) this.searchInput.focus();
      }

      restoreFiltersFromUrl(announce) {
        const url = new URL(window.location.href);
        const requestedCategory = this.normalizeCategory(url.searchParams.get('category'));
        const categoryExists = this.categoryButtons.some((button) => {
          return button.dataset.templateCategoryFilter === requestedCategory;
        });

        this.activeCategory = requestedCategory && categoryExists ? requestedCategory : 'all';
        if (this.searchInput) this.searchInput.value = url.searchParams.get('q') || '';
        this.categoryButtons.forEach((button) => {
          const isActive = button.dataset.templateCategoryFilter === this.activeCategory;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        this.applyFilters(announce);
        this.syncUrlState();
      }

      syncUrlState() {
        if ((window.Shopify && window.Shopify.designMode) || typeof window.history.replaceState !== 'function') {
          this.updateDetailLinks();
          return;
        }

        const url = new URL(window.location.href);
        const query = this.searchInput ? this.searchInput.value.trim() : '';
        if (query) {
          url.searchParams.set('q', query);
        } else {
          url.searchParams.delete('q');
        }
        if (this.activeCategory && this.activeCategory !== 'all') {
          url.searchParams.set('category', this.activeCategory);
        } else {
          url.searchParams.delete('category');
        }
        window.history.replaceState(window.history.state, '', url.toString());
        this.updateDetailLinks();
      }

      updateDetailLinks() {
        const query = this.searchInput ? this.searchInput.value.trim() : '';
        const category = this.activeCategory !== 'all' ? this.activeCategory : '';
        this.detailLinks.forEach((link) => {
          const baseUrl = link.dataset.templateDetailUrl;
          if (!baseUrl) return;
          const url = new URL(baseUrl, window.location.origin);
          if (query) url.searchParams.set('library_q', query);
          if (category) url.searchParams.set('library_category', category);
          link.href = url.toString();
        });
      }

      applyFilters(announce) {
        const query = this.normalize(this.searchInput ? this.searchInput.value : '');
        let visibleCount = 0;
        const slidersToUpdate = [];

        this.cards.forEach((card) => {
          const categoryMatches = this.activeCategory === 'all' || card.dataset.templateCategory === this.activeCategory;
          const searchText = this.normalize(card.dataset.templateSearch || card.textContent || '');
          const queryMatches = query === '' || searchText.includes(query);
          const isVisible = categoryMatches && queryMatches;
          const wasHidden = card.hidden;
          card.hidden = !isVisible;
          if (isVisible) {
            visibleCount += 1;
            if (wasHidden) {
              const slider = card.querySelector('[data-template-preview-slider]');
              if (slider) slidersToUpdate.push(slider);
            }
          }
        });

        if (slidersToUpdate.length) {
          requestAnimationFrame(() => {
            slidersToUpdate.forEach((slider) => {
              if (slider.globalSlide && typeof slider.globalSlide.update === 'function') {
                slider.globalSlide.update();
              }
            });
          });
        }

        if (this.clearButton) this.clearButton.hidden = query === '';
        if (this.noResults) this.noResults.hidden = visibleCount !== 0;
        if (this.resultCount) {
          this.resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? 'template' : 'templates'}`;
          if (!announce) this.resultCount.setAttribute('aria-live', 'off');
          requestAnimationFrame(() => {
            if (this.resultCount) this.resultCount.setAttribute('aria-live', 'polite');
          });
        }
      }

      normalize(value) {
        return String(value).trim().toLocaleLowerCase();
      }

      normalizeCategory(value) {
        const category = this.normalize(value);
        return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(category) ? category : '';
      }
    }

    customElements.define('plaud-template-library', PlaudTemplateLibrary);
  }

  function defineTemplateDetail() {
    if (customElements.get('plaud-template-detail')) return;

    class PlaudTemplateDetail extends HTMLElement {
      constructor() {
        super();
        this.previewImages = [];
        this.backLink = null;
        this.handleImageError = this.handleImageError.bind(this);
      }

      connectedCallback() {
        this.backLink = this.querySelector('[data-template-library-back]');
        this.restoreLibraryContext();
        this.previewImages = Array.from(this.querySelectorAll('[data-template-preview] img'));
        this.previewImages.forEach((image) => {
          image.addEventListener('error', this.handleImageError);
          if (image.complete && image.naturalWidth === 0) showPreviewImageFallback(image);
        });
      }

      disconnectedCallback() {
        this.previewImages.forEach((image) => image.removeEventListener('error', this.handleImageError));
        this.previewImages = [];
        this.backLink = null;
      }

      handleImageError(event) {
        showPreviewImageFallback(event.target);
      }

      restoreLibraryContext() {
        if (!this.backLink || !this.backLink.dataset.templateLibraryUrl) return;
        const currentUrl = new URL(window.location.href);
        const libraryUrl = new URL(this.backLink.dataset.templateLibraryUrl, window.location.origin);
        const query = currentUrl.searchParams.get('library_q');
        const category = currentUrl.searchParams.get('library_category');
        if (query) libraryUrl.searchParams.set('q', query);
        if (category && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(category)) {
          libraryUrl.searchParams.set('category', category);
        }
        this.backLink.href = libraryUrl.toString();
      }
    }

    customElements.define('plaud-template-detail', PlaudTemplateDetail);
  }

  defineTemplateLibrary();
  defineTemplateDetail();
})();
